import { ChefHat, ChevronRight, Plus, Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useRecipes } from '../api/hooks';
import type { RecipeDraft } from '../components/recipes/draft';
import { ImportRecipeSheet } from '../components/recipes/ImportRecipeSheet';
import { LazyRecipeEditor } from '../components/recipes/LazyRecipeEditor';
import { kcal, pluralize } from '../lib/format';
import { matchRecipes } from '../lib/recipeList';

/** Your recipes, newest first, with a search and a way to add one. */
export function RecipesPage() {
  const recipes = useRecipes();
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState('');
  // New recipe offers the ways in first (a link, pasted text, a photo, from scratch);
  // each opens the editor with its draft.
  const [choosing, setChoosing] = useState(false);
  const [draft, setDraft] = useState<RecipeDraft | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const list = useMemo(() => recipes.data ?? [], [recipes.data]);
  const q = query.trim();
  // Matches keep the list's order (newest first).
  const shown = useMemo(() => matchRecipes(list, q), [list, q]);

  // Coming back after deleting a recipe: focus lands here instead of the top of the page.
  useEffect(() => {
    if ((location.state as { focusHeading?: boolean } | null)?.focusHeading) headingRef.current?.focus();
  }, [location.state]);

  const startNew = () => setChoosing(true);

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1 ref={headingRef} tabIndex={-1} className="page__title">
            Recipes
          </h1>
          <p className="page__sub">Dishes you make, ready to log one serving at a time.</p>
        </div>
        {list.length > 0 && (
          <button className="btn btn--primary btn--sm" onClick={startNew}>
            <Plus size={16} aria-hidden="true" /> New recipe
          </button>
        )}
      </div>

      {recipes.isPending ? (
        <div className="card card--pad stack" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className="skeleton" style={{ height: 52 }} />
          ))}
        </div>
      ) : recipes.isError && !recipes.data ? (
        <div className="card card--pad stack">
          <p className="field__error" role="alert">
            {recipes.error.message}
          </p>
          <button className="btn btn--ghost btn--sm" style={{ alignSelf: 'flex-start' }} onClick={() => void recipes.refetch()}>
            Try again
          </button>
        </div>
      ) : list.length === 0 ? (
        <section className="card card--pad">
          <div className="empty">
            <div className="empty__icon">
              <ChefHat size={22} aria-hidden="true" />
            </div>
            <div className="stack" style={{ gap: 4 }}>
              <b style={{ color: 'var(--ink)' }}>No recipes yet</b>
              <p className="small">Save a dish you make often. Then log a serving in one tap, or find it when you search in Log food.</p>
            </div>
            <button className="btn btn--primary" onClick={startNew}>
              <Plus size={17} aria-hidden="true" /> New recipe
            </button>
          </div>
        </section>
      ) : (
        <section className="card card--pad stack" style={{ gap: 10 }} aria-label="Your recipes">
          <div className="input-affix">
            <input
              className="input"
              type="search"
              placeholder="Search your recipes"
              aria-label="Search your recipes"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <span className="input-affix__suffix">
              <Search size={17} aria-hidden="true" />
            </span>
          </div>
          {/* Always there (out of the way while there are matches), so screen readers hear when a search finds nothing. */}
          <p className={shown.length > 0 ? 'sr-only' : 'small muted'} role="status">
            {shown.length === 0 ? `No recipes match “${q}”.` : ''}
          </p>
          {shown.length > 0 && (
            <ul className="recipe-list">
              {shown.map((r) => (
                <li key={r.id}>
                  <Link to={`/recipes/${r.id}`} className="recipe-row">
                    <span className="grow">
                      <span className="recipe-row__name">{r.name}</span>
                      <span className="entry__meta num">
                        {pluralize(r.servings, 'serving')} · {pluralize(r.ingredientCount, 'ingredient')}
                      </span>
                      {(r.copy?.updateAvailable || r.shared) && (
                        <span className="recipe-row__tags">
                          {r.copy?.updateAvailable && <span className="badge badge--ai">Update available</span>}
                          {r.shared && <span className="badge">Shared</span>}
                        </span>
                      )}
                    </span>
                    <span className="recipe-row__kcal num">
                      <span>
                        <b>{kcal(r.perServing.kcal)}</b> kcal
                      </span>
                      <span className="tiny muted">per serving</span>
                    </span>
                    <ChevronRight size={18} className="muted" aria-hidden="true" style={{ flex: 'none' }} />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {choosing && (
        <ImportRecipeSheet
          onClose={() => setChoosing(false)}
          onDraft={(next) => {
            setChoosing(false);
            setDraft(next);
          }}
        />
      )}

      {draft && (
        <LazyRecipeEditor
          initial={draft}
          onClose={() => setDraft(null)}
          onSaved={(recipe) => {
            setDraft(null);
            navigate(`/recipes/${recipe.id}`, { state: { focusHeading: true } });
          }}
        />
      )}
    </div>
  );
}
